import { useTranslation } from "@coworker/locales";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import {
  FilterType,
  FilterValue,
  InputType,
  SelectFilter,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { Article, Combined } from "../types/article";
import { sortArticlesByItemName } from "../../../helpers/sorters";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import { UNKNOWN_ITEM_NAME } from "@coworker/apprestructured/src/shared/constants/itemType";

const SORT_ID = "sort";

function useSortSelectFilter(filterValues: FilterValue[]): SelectFilter {
  const { t } = useTranslation();
  const defaultValue = {
    filterId: SORT_ID,
    value: "byName",
  };
  const selected = getSelectedFilterValue(SORT_ID, filterValues, defaultValue);
  const options = [
    { value: "byName", title: t("nameString") },
    { value: "newestFirst", title: t("newestFirstString") },
    { value: "oldestFirst", title: t("oldestFirstString") },
  ];
  const selectedOption = options.find(
    (option) => option.value === selected.value
  )!;

  return {
    inputType: InputType.Select,
    filterType: FilterType.Sort,
    id: SORT_ID,
    title: t("sortByFilterTitleString", { option: selectedOption.title }),
    options,
    enabled: true,
    defaultValue,
  };
}

function compareDate(item1: Combined<Article>, item2: Combined<Article>) {
  return (
    (item1.createdAtDate?.getTime() ?? 0) -
    (item2.createdAtDate?.getTime() ?? 0)
  );
}

function compareProductArticleIdy(
  item1: Combined<Article>,
  item2: Combined<Article>
) {
  return item1.productArticleId.localeCompare(item2.productArticleId);
}

function compareByName(item1: Combined<Article>, item2: Combined<Article>) {
  const alphabeticCompare = sortArticlesByItemName(item1, item2);
  if (alphabeticCompare !== 0) {
    return alphabeticCompare;
  }
  return compareProductArticleIdy(item1, item2);
}

function compareByDate(item1: Combined<Article>, item2: Combined<Article>) {
  const dateComapre = compareDate(item1, item2);
  if (dateComapre !== 0) {
    return dateComapre;
  }
  return compareProductArticleIdy(item1, item2);
}

function pushUnknownToBottom<T>(
  item1: Combined<T>,
  item2: Combined<T>,
  isUnknown: (item: Combined<T>) => boolean,
  compareFn: (a: Combined<T>, b: Combined<T>) => number
) {
  const aUnknown = isUnknown(item1);
  const bUnknown = isUnknown(item2);

  if (aUnknown && !bUnknown) return 1;
  if (!aUnknown && bUnknown) return -1;
  return compareFn(item1, item2);
}

const isNameUnknown = (item: Combined<Article>) =>
  !item.itemName || item.itemName.toLowerCase() === UNKNOWN_ITEM_NAME;

function useSortSelectArticleFilter(filterValues: FilterValue[]) {
  const filter: ApplyFilter<SelectFilter, Combined<Article>> = {
    filter: useSortSelectFilter(filterValues),
    sortCompare: (
      item1: Combined<Article>,
      item2: Combined<Article>,
      { value }: FilterValue
    ) => {
      switch (value) {
        case "newestFirst":
          return pushUnknownToBottom(
            item1,
            item2,
            isNameUnknown,
            (a, b) => -compareByDate(a, b)
          );
        case "oldestFirst":
          return pushUnknownToBottom(
            item1,
            item2,
            isNameUnknown,
            compareByDate
          );
        case "byName":
        default:
          return pushUnknownToBottom(
            item1,
            item2,
            isNameUnknown,
            compareByName
          );
      }
    },
  };

  return filter;
}

export { useSortSelectArticleFilter };
